import {
	type PointerEvent as ReactPointerEvent,
	type RefObject,
	useEffect,
	useRef,
	useState,
} from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import { Tab } from '@/libraries/design/patterns/tabs/wac-refresh-tabs/src/tab';
import { TabList } from '@/libraries/design/patterns/tabs/wac-refresh-tabs/src/tab-list';
import { TabPanel } from '@/libraries/design/patterns/tabs/wac-refresh-tabs/src/tab-panel';

import { JsmPortalPlaygroundHrPreview } from './components/hr/jsm-portal-playground-hr-preview';
import { JsmPortalPlaygroundItSupportPreview } from './components/it-support/jsm-portal-playground-it-support-preview';
import { JsmPlaygroundCursor, type CursorColor } from './components/jsm-playground-cursor';
import { JsmPlaygroundDemoLayerContext } from './components/jsm-playground-demo-layer';
import { JsmPlaygroundNavigation } from './components/jsm-playground-navigation';
import { JsmPlaygroundEngineering } from './engineering/jsm-playground-engineering';
import { JsmPlaygroundItOperations } from './jsm-playground-it-operations';
import { teamNavigationConfig, teamTabs, type TeamTabValue } from './jsm-portal-playground-data';
import { jsmPortalPlaygroundMessages as messages } from './jsm-portal-playground-messages';

const PREVIEW_DESIGN_WIDTH = 1168;
const PREVIEW_DESIGN_HEIGHT = 750;

const usePreviewScale = (): [RefObject<HTMLDivElement>, number] => {
	const ref = useRef<HTMLDivElement>(null);
	const [scale, setScale] = useState(1);

	useEffect(() => {
		const element = ref.current;
		if (!element) {
			return;
		}

		const measure = () => {
			const width = element.clientWidth;
			if (width > 0) {
				setScale(Math.min(1, width / PREVIEW_DESIGN_WIDTH));
			}
		};

		const observer = new ResizeObserver(measure);
		observer.observe(element);
		measure();

		return () => observer.disconnect();
	}, []);

	return [ref, scale];
};

const DEMO_START_VISIBILITY = 0.5;

const useHasBeenSeen = (ref: RefObject<HTMLElement>) => {
	const [hasBeenSeen, setHasBeenSeen] = useState(false);

	useEffect(() => {
		const element = ref.current;
		if (!element || hasBeenSeen) {
			return;
		}
		if (typeof IntersectionObserver === 'undefined') {
			setHasBeenSeen(true);
			return;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) {
					setHasBeenSeen(true);
				}
			},
			{ threshold: DEMO_START_VISIBILITY },
		);
		observer.observe(element);

		return () => observer.disconnect();
	}, [ref, hasBeenSeen]);

	return hasBeenSeen;
};

export type UniformJsmPortalPlaygroundProps = {
	initialActiveTab?: TeamTabValue;
	cursorLabel?: string;
	cursorColor?: CursorColor;
};

export const UniformJsmPortalPlayground = ({
	initialActiveTab = 'it-support',
	cursorLabel,
	cursorColor = 'yellow',
}: UniformJsmPortalPlaygroundProps = {}) => {
	const intl = useIntl();
	const [activeTab, setActiveTab] = useState<TeamTabValue>(initialActiveTab);
	const [isCursorActive, setIsCursorActive] = useState(false);
	const [isCursorHovering, setIsCursorHovering] = useState(false);
	const [isCursorClicking, setIsCursorClicking] = useState(false);
	const [hasUserTakenOver, setHasUserTakenOver] = useState(false);
	const cursorRef = useRef<HTMLDivElement>(null);
	const isPointerInsidePreviewRef = useRef(false);
	const isHoveringInteractiveRef = useRef(false);
	const [previewColumnRef, previewScale] = usePreviewScale();
	const hasPreviewBeenSeen = useHasBeenSeen(previewColumnRef);

	const updateCursorPosition = (event: ReactPointerEvent<HTMLElement>) => {
		const cursor = cursorRef.current;

		if (!cursor) {
			return;
		}

		const sectionBounds = event.currentTarget.getBoundingClientRect();
		cursor.style.setProperty('--cursor-x', `${event.clientX - sectionBounds.left}px`);
		cursor.style.setProperty('--cursor-y', `${event.clientY - sectionBounds.top}px`);

		const isClickable = isClickableElement(event.target);
		if (isClickable !== isHoveringInteractiveRef.current) {
			isHoveringInteractiveRef.current = isClickable;
			setIsCursorHovering(isClickable);
		}
	};

	const handlePreviewPointerEnter = (event: ReactPointerEvent<HTMLDivElement>) => {
		isPointerInsidePreviewRef.current = true;
		updateCursorPosition(event);
		setIsCursorActive(true);
	};

	const handlePreviewPointerLeave = () => {
		isPointerInsidePreviewRef.current = false;
		isHoveringInteractiveRef.current = false;
		setIsCursorActive(false);
		setIsCursorHovering(false);
		setIsCursorClicking(false);
	};

	const handlePreviewFocusChange = () => {
		if (!isPointerInsidePreviewRef.current) {
			isHoveringInteractiveRef.current = false;
			setIsCursorActive(false);
			setIsCursorHovering(false);
			setIsCursorClicking(false);
		}
	};

	const handlePreviewFocus = () => {
		setHasUserTakenOver(true);
		handlePreviewFocusChange();
	};

	const handlePreviewKeyDown = () => {
		setHasUserTakenOver(true);
	};

	const handlePreviewPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
		setHasUserTakenOver(true);
		if (isClickableElement(event.target)) {
			setIsCursorClicking(true);
		}
	};

	const handlePreviewPointerUp = () => {
		setIsCursorClicking(false);
	};

	const [teamVisits, setTeamVisits] = useState<Partial<Record<TeamTabValue, number>>>({});
	const [demoLayer, setDemoLayer] = useState<HTMLDivElement | null>(null);

	const handleTabSelect = (id: string) => {
		const team = id as TeamTabValue;
		if (team !== activeTab) {
			setTeamVisits((visits) => ({ ...visits, [team]: (visits[team] ?? 0) + 1 }));
		}
		setHasUserTakenOver(false);
		setActiveTab(team);
	};

	return (
		<section css={playgroundSectionStyles} aria-label={intl.formatMessage(messages.sectionLabel)}>
			<div css={tabListWrapperStyles}>
				<TabList
					activeTabId={activeTab}
					aria-label={intl.formatMessage(messages.serviceTeamTabListLabel)}
				>
					{teamTabs.map((teamTab, index) => (
						<Tab
							key={teamTab.value}
							tabId={teamTab.value}
							index={index}
							title={intl.formatMessage(teamTab.label)}
							isActive={teamTab.value === activeTab}
							onTabSelect={handleTabSelect}
							tabSize="small"
						/>
					))}
				</TabList>
			</div>

			<div css={previewOuterStyles} ref={previewColumnRef}>
				<JsmPlaygroundCursor
					ref={cursorRef}
					label={cursorLabel ?? intl.formatMessage(messages.customCursorLabel)}
					color={cursorColor}
					isActive={isCursorActive}
					isHovering={isCursorHovering}
					isClicking={isCursorClicking}
				/>
				<div
					css={previewScaleFrameStyles}
					style={{ height: `${PREVIEW_DESIGN_HEIGHT * previewScale}px` }}
				>
					<div
						css={previewScaleInnerStyles}
						data-preview-scale={previewScale}
						style={{ transform: `scale(${previewScale})` }}
					>
						<JsmPlaygroundDemoLayerContext.Provider value={demoLayer}>
							<div
								css={previewCardStyles}
								onBlur={handlePreviewFocusChange}
								onFocus={handlePreviewFocus}
								onKeyDown={handlePreviewKeyDown}
								onPointerDown={handlePreviewPointerDown}
								onPointerEnter={handlePreviewPointerEnter}
								onPointerLeave={handlePreviewPointerLeave}
								onPointerMove={updateCursorPosition}
								onPointerUp={handlePreviewPointerUp}
							>
								<div css={panelStackStyles}>
									{teamTabs.map((teamTab) => (
										<TabPanel
											key={teamTab.value}
											id={teamTab.value}
											isActive={teamTab.value === activeTab}
											// @ts-expect-error -- inert is missing from React types
											inert={teamTab.value === activeTab ? undefined : ''}
										>
											{teamTab.value === 'it-support' && (
												<JsmPortalPlaygroundItSupportPreview
													key={teamVisits['it-support'] ?? 0}
													isActive={activeTab === 'it-support' && hasPreviewBeenSeen}
													hasUserTakenOver={hasUserTakenOver}
												/>
											)}
											{teamTab.value === 'it-operations' && (
												<div css={teamPreviewStyles}>
													<JsmPlaygroundNavigation
														config={teamNavigationConfig[teamTab.value]}
														teamName={intl.formatMessage(teamTab.label)}
													/>
													{activeTab === 'it-operations' && hasPreviewBeenSeen ? (
														<JsmPlaygroundItOperations hasUserTakenOver={hasUserTakenOver} />
													) : (
														<div css={blankPreviewStyles} />
													)}
												</div>
											)}
											{teamTab.value === 'engineering' && (
												<div css={teamPreviewStyles}>
													<JsmPlaygroundNavigation
														config={teamNavigationConfig[teamTab.value]}
														teamName={intl.formatMessage(teamTab.label)}
													/>
													{activeTab === 'engineering' && hasPreviewBeenSeen ? (
														<JsmPlaygroundEngineering hasUserTakenOver={hasUserTakenOver} />
													) : (
														<div css={blankPreviewStyles} />
													)}
												</div>
											)}
											{teamTab.value === 'hr' && (
												<JsmPortalPlaygroundHrPreview
													key={teamVisits.hr ?? 0}
													isActive={activeTab === 'hr' && hasPreviewBeenSeen}
													hasUserTakenOver={hasUserTakenOver}
												/>
											)}
										</TabPanel>
									))}
								</div>
							</div>
						</JsmPlaygroundDemoLayerContext.Provider>
						<div ref={setDemoLayer} css={demoLayerStyles} aria-hidden="true" />
					</div>
				</div>
			</div>
		</section>
	);
};

export default UniformJsmPortalPlayground;

const playgroundSectionStyles = css({
	position: 'relative',
	boxSizing: 'border-box',
	width: '100%',
	overflowX: 'clip',
	paddingBlockStart: token('space.400'),
	paddingBlockEnd: token('space.600'),
	paddingInline: token('space.300'),
	[media.above.md]: {
		paddingInline: token('space.600'),
	},
});

const tabListWrapperStyles = css({
	display: 'flex',
	justifyContent: 'center',
	width: 'fit-content',
	maxWidth: '100%',
	marginInline: 'auto',
	marginBlockEnd: token('space.400'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> div': {
		backgroundColor: `${wacToken('color.neutral.light0')} !important`,
		border: `1px solid ${wacToken('color.primary.black')} !important`,
		[media.below.sm]: {
			backgroundColor: 'transparent !important',
			border: 'none !important',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'a[role="tab"]': {
		paddingInline: `${token('space.250')} !important`,
		fontFamily: wacToken('font.charlie.display'),
		fontWeight: token('font.weight.semibold'),
	},
});

const panelStackStyles = css({
	display: 'grid',
	gridTemplateColumns: 'minmax(0, 1fr)',
	gridTemplateRows: 'minmax(0, 1fr)',
	width: '100%',
	height: '100%',
	minHeight: 0,
});

const previewOuterStyles = css({
	position: 'relative',
	width: '100%',
	maxWidth: `${PREVIEW_DESIGN_WIDTH}px`,
	marginInline: 'auto',
	boxSizing: 'border-box',
});

const previewScaleFrameStyles = css({
	position: 'relative',
	width: '100%',
});

const previewScaleInnerStyles = css({
	width: `${PREVIEW_DESIGN_WIDTH}px`,
	height: `${PREVIEW_DESIGN_HEIGHT}px`,
	transformOrigin: 'top left',
});

const demoLayerStyles = css({
	position: 'absolute',
	inset: 0,
	zIndex: 2,
	pointerEvents: 'none',
});

const previewCardStyles = css({
	position: 'relative',
	zIndex: 1,
	boxSizing: 'border-box',
	width: '100%',
	height: `${PREVIEW_DESIGN_HEIGHT}px`,
	border: `${token('border.width', '1px')} solid ${token('color.border')}`,
	borderRadius: token('radius.xlarge'),
	backgroundColor: token('elevation.surface'),
	boxShadow: token('elevation.shadow.overlay'),
	overflow: 'hidden',
	cursor: 'none',
	font: token(
		'font.body',
		'normal 400 14px/20px "Atlassian Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
	),
	color: token('color.text'),
	'--ds-font-family-sans':
		'"Atlassian Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
	'--ds-font-body':
		'normal 400 14px/20px "Atlassian Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
	'--ds-font-weight-bold': '600',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'*': {
		cursor: 'none !important',
	},
	'@media (pointer: coarse)': {
		cursor: 'auto',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'*': {
			cursor: 'auto !important',
		},
	},
});

const blankPreviewStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flex: '1 1 auto',
	width: '100%',
	borderRadius: token('radius.xlarge'),
	backgroundColor: token('elevation.surface'),
});

const teamPreviewStyles = css({
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	height: '100%',
	borderRadius: token('radius.xlarge'),
	overflow: 'hidden',
});

const isClickableElement = (target: EventTarget | null): boolean => {
	if (!(target instanceof HTMLElement)) {
		return false;
	}

	const clickableSelector = [
		'button',
		'a[href]',
		'input:not([disabled])',
		'select:not([disabled])',
		'textarea:not([disabled])',
		'label',
		'[role="button"]',
		'[role="checkbox"]',
		'[role="tab"]',
		'[role="link"]',
		'[data-clickable="true"]',
	].join(', ');

	return Boolean(target.closest(clickableSelector));
};
